<div class="set-stack org">
  <!-- Who you are: logo, name and the identifiers support asks for -->
  <section class="bk-panel set-panel org__id" aria-labelledby="org-name">
    <div class="org__logo">
      @if (canEdit) {
        <image-input
          label=""
          ariaLabel="Organization logo"
          [previewUrl]="institutionLogo"
          [isLoading]="isUploadingLogo || isDeletingLogo"
          (imageSelected)="addLogo($event)"
          (imageDeleted)="deleteLogo()"
          roundedClass="rounded-2xl"
          sizeClass="size-20"
        />
      } @else {
        <span class="org__tile" aria-hidden="true">
          @if (institutionLogo) {
            <img [src]="institutionLogo" alt="" />
          } @else {
            <span class="bk-sym">domain</span>
          }
        </span>
      }
    </div>

    <div class="org__who">
      <p class="bk-eyebrow">Organization</p>
      @if (loaded || orgName) {
        <h2 class="bk-heading org__name" id="org-name">{{ orgName || 'Your organization' }}</h2>
      } @else {
        <h2 class="bk-heading org__name" id="org-name">
          <span class="bk-skel org__name-skel"></span>
          <span class="bk-sr-only">Loading organization</span>
        </h2>
      }

      @if (facts.length) {
        <dl class="org__facts">
          @for (fact of facts; track fact.label) {
            <div class="org__fact">
              <dt>{{ fact.label }}</dt>
              <dd>
                <span class="org__fact-value">{{ fact.value }}</span>
                <button
                  type="button"
                  class="bk-icon-btn org__copy"
                  [attr.aria-label]="'Copy ' + fact.label"
                  [matTooltip]="'Copy ' + fact.label"
                  (click)="copy(fact)"
                >
                  <span class="bk-sym" aria-hidden="true">content_copy</span>
                </button>
              </dd>
            </div>
          }
        </dl>
      }
    </div>
  </section>

  @if (!canEdit) {
    <p class="set-note" role="note">
      <span class="bk-sym" aria-hidden="true">lock</span>
      <span>
        You can see these details but not change them. Administrators and account managers can edit them.
      </span>
    </p>
  }

  <form class="set-stack" [formGroup]="institutionForm" (ngSubmit)="handleSubmit()" novalidate>
    <!-- Contact -->
    <section class="bk-panel set-panel" aria-labelledby="org-contact-title">
      <header class="set-panel__head">
        <div class="set-panel__intro">
          <h3 class="set-panel__title" id="org-contact-title">Contact</h3>
          <p class="set-panel__sub">Where customers and the Black Konnect support team can reach your organization.</p>
        </div>
      </header>

      <div class="set-grid">
        <input-field
          label="Phone number"
          [errorMessage]="
            submitted && institutionForm.controls.primaryContactNumber.invalid ? 'Enter a valid phone number.' : ''
          "
        >
          <ngx-mat-input-tel
            formControlName="primaryContactNumber"
            [enablePlaceholder]="true"
            [preferredCountries]="['bw', 'za']"
            [enableSearch]="true"
          />
        </input-field>

        <input-field label="Email address" [errorMessage]="submitted ? emailErrorMsg : ''">
          <input
            class="input-style"
            type="email"
            autocomplete="email"
            placeholder="support@company.com"
            formControlName="emailAddress"
          />
        </input-field>

        <input-field class="is-wide" label="Website">
          <input
            class="input-style"
            type="url"
            autocomplete="url"
            placeholder="https://company.com"
            formControlName="website"
          />
        </input-field>
      </div>
    </section>

    <!-- Address -->
    <section class="bk-panel set-panel" aria-labelledby="org-address-title">
      <header class="set-panel__head">
        <div class="set-panel__intro">
          <h3 class="set-panel__title" id="org-address-title">Address</h3>
          <p class="set-panel__sub">Your registered address. The country also sets the country code.</p>
        </div>
      </header>

      <div class="set-grid">
        <input-field
          label="Country"
          [errorMessage]="submitted && institutionForm.controls.country.invalid ? 'Choose a country.' : ''"
        >
          <select
            name="country"
            id="org-country"
            class="input-style"
            formControlName="country"
            (change)="updateCountryCode()"
          >
            <option value="" disabled>Choose a country</option>
            @for (country of countries; track country) {
              <option [value]="country">{{ country }}</option>
            }
          </select>
        </input-field>

        <div class="set-fact">
          <span class="set-fact__label">Country code</span>
          <span class="set-fact__value">{{ countryCode || '—' }}</span>
          <span class="set-fact__hint">Set from the country.</span>
        </div>

        <input-field label="Street address">
          <input
            class="input-style"
            type="text"
            autocomplete="street-address"
            placeholder="Building, street and number"
            formControlName="address"
          />
        </input-field>

        <input-field label="City">
          <input
            class="input-style"
            type="text"
            autocomplete="address-level2"
            placeholder="City or town"
            formControlName="city"
          />
        </input-field>
      </div>
    </section>

    <!-- Business -->
    <section class="bk-panel set-panel" aria-labelledby="org-business-title">
      <header class="set-panel__head">
        <div class="set-panel__intro">
          <h3 class="set-panel__title" id="org-business-title">Business</h3>
          <p class="set-panel__sub">What your organization does. Used when Black Konnect reviews sender IDs.</p>
        </div>
      </header>

      <div class="set-grid">
        <input-field
          label="Industry"
          [errorMessage]="submitted && institutionForm.controls.industryType.invalid ? 'Choose an industry.' : ''"
        >
          <select name="industry" id="org-industry" class="input-style" formControlName="industryType">
            <option value="" disabled>Choose an industry</option>
            @for (industry of industries; track industry.value) {
              <option [value]="industry.value">{{ industry.label }}</option>
            }
          </select>
        </input-field>

        <input-field label="Registration number">
          <input
            class="input-style"
            type="text"
            placeholder="Company registration number"
            formControlName="registrationNumber"
          />
        </input-field>

        <input-field
          class="is-wide"
          label="About"
          hint="One or two sentences on what you do and who you message."
          [errorMessage]="submitted && institutionForm.controls.description.invalid ? 'Describe your organization.' : ''"
        >
          <textarea
            class="input-style"
            rows="4"
            placeholder="For example, a school group that sends fee reminders and event notices to parents"
            formControlName="description"
          ></textarea>
        </input-field>
      </div>
    </section>

    <!-- Save bar: stays in view while there is something to save -->
    @if (canEdit && (hasChanges || isUpdatingOrganization || saveError)) {
      <div class="org__save" role="region" aria-label="Save organization details">
        @if (saveError) {
          <p class="org__save-error" role="alert">
            <span class="bk-sym" aria-hidden="true">error</span>
            <span>{{ saveError }}</span>
          </p>
        } @else {
          <p class="org__save-note">You have unsaved changes.</p>
        }
        <div class="org__save-actions">
          <button
            type="button"
            class="bk-btn bk-btn--ghost"
            (click)="discardChanges()"
            [disabled]="isUpdatingOrganization"
          >
            Discard
          </button>
          <customizable-button
            title="Save changes"
            className="w-full"
            type="submit"
            [loading]="isUpdatingOrganization"
            [disabled]="isUpdatingOrganization"
          />
        </div>
      </div>
    }
  </form>

  <!-- Messaging identity: what your messages go out as -->
  <section class="bk-panel set-panel" aria-labelledby="org-msg-title">
    <header class="set-panel__head">
      <div class="set-panel__intro">
        <h3 class="set-panel__title" id="org-msg-title">Messaging identity</h3>
        <p class="set-panel__sub">
          The sender IDs your SMS can go out from. Recipients see the sender ID in place of a phone number. To add
          one, contact Black Konnect support.
        </p>
      </div>
    </header>

    @if (senderIdsLoading) {
      <div class="org__senders-skel" aria-hidden="true">
        @for (n of [1, 2, 3]; track n) {
          <span class="bk-skel"></span>
        }
      </div>
      <span class="bk-sr-only" role="status">Loading sender IDs</span>
    } @else if (senderIdsFailed) {
      <p class="set-note">
        <span class="bk-sym" aria-hidden="true">error</span>
        <span>
          The sender IDs did not load.
          <button type="button" class="bk-more org__retry" (click)="retrySenderIds()">Try again</button>
        </span>
      </p>
    } @else if (senderRows.length === 0) {
      <p class="set-note">
        <span class="bk-sym" aria-hidden="true">badge</span>
        <span>
          Your organization has no sender IDs yet. Messages cannot go out until one is set up. Contact Black Konnect
          support to request one.
        </span>
      </p>
    } @else {
      <div class="org__senders">
        <table class="bk-log">
          <thead>
            <tr>
              <th scope="col">Sender ID</th>
              <th scope="col" class="col-kind">Type</th>
              <th scope="col">Status</th>
              <th scope="col" class="col-added">Added</th>
            </tr>
          </thead>
          <tbody>
            @for (row of senderRows; track row.key) {
              <tr>
                <td class="org__sender">{{ row.name }}</td>
                <td class="quiet col-kind">{{ row.kind }}</td>
                <td><app-status [text]="row.statusText" [status]="row.status" /></td>
                <td class="quiet col-added">{{ row.added }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>
</div>
